import {
  ArrowUpRight,
  Building2,
  ChartNoAxesCombined,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Inbox,
  Loader2,
  RefreshCw,
  Shield,
  Target,
  Users,
  X,
} from 'lucide-react'
import { useAuth } from '@clerk/clerk-react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/components/layout/sidebar'
import { Button } from '@/components/ui/button'
import { OpenCompanyNav } from '@/components/layout/sidebar/open-company-nav'
import { SidebarUser } from '@/components/layout/sidebar/sidebar-user'
import { CornerBottomIcon } from '@/components/ui/corner-bottom-icon'
import { Typography } from '@/components/ui/typography'
import { postToOgsmSsoBridge } from '@/lib/ogsm-sync-url'
import { isActualVsBudgetPortfolioEnabled } from '@/features/actual-vs-budget-production/access'
import { isDealReviewEnabled } from '@/features/deal-review/access'
import { cn } from '@/lib/utils'
import { useViews } from '@/hooks/use-views'
import {
  selectAllowedApps,
  selectIsInternal,
  selectCapabilities,
  useUserStore,
} from '@/stores/user.store'
import logoImg from '@/assets/images/greybull-logo.svg'
import type { LucideIcon } from 'lucide-react'
import type { ViewEntityType } from '@/types/api'

interface NavItem {
  title: string
  url: string
  icon: LucideIcon
  entityType: ViewEntityType
  basePath: string
}

interface SimpleNavItem {
  title: string
  url: string
  icon?: LucideIcon
  internalOnly?: boolean
  requiredApp?: string
  external?: boolean
  /** Capability required to show this item (e.g., 'admin:settings:manage') */
  requiredCapability?: string
  /** When true, render as a button that posts a Clerk JWT to OGSM's SSO
   * bridge instead of a static link. The `url` field is ignored. */
  bridgeToOgsm?: boolean
}

// Navigation items configuration - views will be fetched dynamically
const navItems: NavItem[] = [
  {
    title: 'Companies',
    url: '/companies',
    icon: Building2,
    entityType: 'COMPANY',
    basePath: '/companies/views',
  },
]

// Simple navigation items without views
const simpleNavItems: SimpleNavItem[] = [
  ...(isActualVsBudgetPortfolioEnabled(import.meta.env)
    ? [
        {
          title: 'Actual vs Budget',
          url: '/actual-vs-budget',
          icon: ChartNoAxesCombined,
          internalOnly: true,
        },
      ]
    : []),
  {
    title: 'Contacts',
    url: '/contacts',
    icon: Users,
  },
  ...(isDealReviewEnabled(import.meta.env)
    ? [
        {
          // Deal review is Greybull-internal. The backend answers 404 to every
          // external identity, so hiding the entry is presentation, not
          // security, and so is the flag around it.
          title: 'Deal Review',
          url: '/deal-review',
          icon: Inbox,
          internalOnly: true,
          requiredCapability: 'deal-review:view',
        },
      ]
    : []),
  {
    title: 'Chronograph Sync',
    url: '/admin/chronograph',
    icon: RefreshCw,
    requiredCapability: 'admin:settings:manage',
  },
  {
    title: 'User Management',
    url: '/admin/users',
    icon: Shield,
    requiredCapability: 'admin:users:manage',
  },
]

/**
 * Sidebar nav item with dynamic view subitems
 */
function NavItemWithViews({ item }: { item: NavItem }) {
  const navigate = useNavigate()
  const { views, isLoading } = useViews(item.entityType)
  const { state, setOpenMobile } = useSidebar()

  // Get current viewId from route params
  const params = useParams({ strict: false })
  const currentViewId = (params as { viewId?: string }).viewId

  // Map views to sidebar items
  const subItems = views.map((view) => ({
    id: view.id,
    title: view.name,
    url: `${item.basePath}/${view.id}`,
    isDefault: view.isDefault,
    isActive: view.id === currentViewId,
  }))

  // Check if any child view is active (for parent icon highlighting)
  const hasActiveChild = subItems.some((subItem) => subItem.isActive)

  const handleNavigate = (url: string) => {
    navigate({ to: url })
    setOpenMobile(false)
  }

  // Collapsed menu content (shown in tooltip/dropdown)
  const collapsedMenuContent =
    subItems.length > 0 ? (
      <DropdownMenuGroup className="flex flex-col">
        <DropdownMenuLabel className="text-sm font-semibold text-primary pb-1">
          {item.title}
        </DropdownMenuLabel>
        <Separator className="bg-primary/20 mb-2" />
        <div className="flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            subItems.map((subItem) => (
              <DropdownMenuItem key={subItem.url} asChild>
                <Button
                  variant="ghost"
                  onClick={() => handleNavigate(subItem.url)}
                  className={cn(
                    'flex w-full items-center justify-start h-auto py-1 px-1.5 text-primary hover:!bg-secondary/10 hover:!text-secondary cursor-pointer',
                    subItem.isActive && '!bg-secondary/10 !text-secondary'
                  )}
                >
                  <span className="truncate">{subItem.title}</span>
                </Button>
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuGroup>
    ) : (
      item.title
    )

  const collapsedMenu =
    state === 'collapsed'
      ? {
          content: collapsedMenuContent,
          contentClassName: subItems.length > 0 ? undefined : 'p-1',
        }
      : item.title

  return (
    <Collapsible asChild defaultOpen className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={collapsedMenu}
            className={cn(
              'rounded-sm text-primary/70 hover:!text-secondary hover:!bg-secondary/10 group/parent cursor-pointer',
              // Only show active state on parent when collapsed (subitems not visible)
              state === 'collapsed' &&
                hasActiveChild &&
                '!text-secondary !bg-secondary/10'
            )}
          >
            {item.icon && (
              <item.icon
                className={cn(
                  'text-primary/70 group-hover/parent:text-secondary transition-colors',
                  state === 'collapsed' && hasActiveChild && '!text-secondary'
                )}
              />
            )}
            <span>{item.title}</span>
            <ChevronRight
              className={cn(
                'ml-auto text-primary/70 group-hover/parent:text-secondary transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90'
              )}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {isLoading ? (
              <SidebarMenuSubItem>
                <div className="flex items-center gap-2 px-2 py-1.5 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span className="text-xs">Loading views...</span>
                </div>
              </SidebarMenuSubItem>
            ) : subItems.length === 0 ? (
              <SidebarMenuSubItem>
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  No views available
                </div>
              </SidebarMenuSubItem>
            ) : (
              subItems.map((subItem) => (
                <SidebarMenuSubItem key={subItem.url}>
                  <SidebarMenuSubButton
                    asChild
                    className={cn(
                      'rounded-sm text-primary/70 hover:text-secondary hover:bg-secondary/10 group/subitem',
                      subItem.isActive && 'text-secondary bg-secondary/10'
                    )}
                  >
                    <Button
                      variant="ghost"
                      onClick={() => handleNavigate(subItem.url)}
                      className="flex items-center justify-start gap-2 w-full h-auto p-0 text-left hover:bg-transparent"
                    >
                      <CornerBottomIcon
                        className={cn(
                          'size-4 text-primary group-hover/subitem:text-secondary',
                          subItem.isActive && 'text-secondary'
                        )}
                      />
                      <span className="truncate">{subItem.title}</span>
                    </Button>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))
            )}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

/**
 * Simple sidebar nav item without subitems
 */
function SimpleNavItem({ item }: { item: SimpleNavItem }) {
  const { getToken } = useAuth()
  const { setOpenMobile } = useSidebar()

  const content = (
    <>
      {item.icon && (
        <item.icon className="text-primary/70 group-hover/parent:text-secondary transition-colors" />
      )}
      <span>{item.title}</span>
      {item.external ? (
        <ArrowUpRight className="ml-auto size-4 text-primary/70 group-hover/parent:text-secondary transition-colors" />
      ) : null}
    </>
  )

  if (item.bridgeToOgsm) {
    const handleClick = () => {
      setOpenMobile(false)
      void postToOgsmSsoBridge({
        getToken,
        ogsmBaseUrl: import.meta.env.VITE_OGSM_URL,
        openInNewTab: true,
      }).catch((err) => {
        console.error('OGSM SSO bridge failed', err)
      })
    }

    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={item.title}
          onClick={handleClick}
          className={cn(
            'rounded-sm text-primary/70 hover:!text-secondary hover:!bg-secondary/10 group/parent cursor-pointer'
          )}
        >
          {content}
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        tooltip={item.title}
        className={cn(
          'rounded-sm text-primary/70 hover:!text-secondary hover:!bg-secondary/10 group/parent cursor-pointer'
        )}
      >
        {item.external ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpenMobile(false)}
          >
            {content}
          </a>
        ) : (
          <Link to={item.url} onClick={() => setOpenMobile(false)}>
            {content}
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { state, toggleSidebar, openMobile, isMobile, setOpenMobile } =
    useSidebar()
  const isInternalUser = useUserStore(selectIsInternal)
  const capabilities = useUserStore(selectCapabilities)
  const allowedApps = useUserStore(selectAllowedApps)
  const ogsmConfigured = Boolean(import.meta.env.VITE_OGSM_URL?.trim())

  const isCollapsed = isMobile ? !openMobile : state === 'collapsed'
  const ogsmNavItem: SimpleNavItem[] = ogsmConfigured
    ? [
        {
          title: 'OGSM',
          url: '#',
          icon: Target,
          requiredApp: 'ogsm',
          external: true,
          bridgeToOgsm: true,
        },
      ]
    : []
  const visibleSimpleNavItems = [...simpleNavItems, ...ogsmNavItem].filter(
    (item) => {
      if (item.internalOnly && !isInternalUser) return false
      if (item.requiredApp && !allowedApps.includes(item.requiredApp))
        return false
      if (
        item.requiredCapability &&
        !capabilities.includes(item.requiredCapability)
      )
        return false
      return true
    }
  )

  const handleToggle = (event?: React.MouseEvent) => {
    event?.stopPropagation()
    toggleSidebar()
  }

  return (
    <Sidebar
      collapsible="icon"
      {...props}
      className={cn(
        'py-6 bg-sidebar border-r border-sidebar-border',
        isCollapsed ? null : 'px-1'
      )}
    >
      <SidebarHeader className="py-2! lg:py-0!">
        <SidebarMenu className="gap-6">
          <SidebarMenuItem>
            {isCollapsed ? (
              <div className="w-full flex flex-col items-center justify-center gap-1">
                <SidebarMenuButton
                  className="flex items-center justify-center p-0! group-data-[collapsible=icon]:p-0! [&>svg]:size-5 hover:bg-transparent active:bg-transparent focus:bg-transparent hover:cursor-pointer transition-opacity duration-50"
                  onClick={handleToggle}
                >
                  <img
                    src={logoImg}
                    alt="Compass Logo"
                    className="h-8 w-auto transition-opacity duration-50"
                  />
                </SidebarMenuButton>
                <Button
                  variant="ghost"
                  aria-label="Expand sidebar"
                  title="Expand sidebar"
                  className="!w-auto !h-auto !p-0 rounded-sm hover:bg-secondary/10 active:bg-transparent focus:bg-transparent hover:cursor-pointer transition-all duration-200 group/trigger"
                  onClick={handleToggle}
                >
                  <ChevronsRight
                    strokeWidth={1}
                    className="min-w-6 min-h-6 text-primary/70 group-hover/trigger:text-secondary transition-colors duration-200"
                  />
                </Button>
              </div>
            ) : (
              <div className="w-full flex items-center justify-between">
                <Link
                  to="/"
                  className="flex items-center gap-2"
                  onClick={() => setOpenMobile(false)}
                >
                  <img
                    src={logoImg}
                    alt="Compass Logo"
                    className="h-10 w-auto"
                  />
                  <Typography variant="h3" as="span">
                    Compass
                  </Typography>
                </Link>
                <Button
                  variant="ghost"
                  aria-label={
                    isMobile ? 'Close navigation menu' : 'Collapse sidebar'
                  }
                  title={
                    isMobile ? 'Close navigation menu' : 'Collapse sidebar'
                  }
                  className={cn(
                    'rounded-sm hover:bg-secondary/10 active:bg-transparent hover:cursor-pointer transition-all duration-200 group/trigger',
                    isMobile
                      ? 'h-11 w-11 p-0 shrink-0'
                      : '!w-auto !h-auto !p-0 focus:bg-transparent'
                  )}
                  onClick={toggleSidebar}
                >
                  {isMobile ? (
                    <X aria-hidden="true" />
                  ) : (
                    <ChevronsLeft
                      strokeWidth={1}
                      className="min-w-6 min-h-6 text-primary/70 group-hover/trigger:text-secondary transition-colors duration-200"
                    />
                  )}
                </Button>
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <div className="px-2 py-6">
        <SidebarSeparator className="bg-primary/10" />
      </div>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => (
                <NavItemWithViews key={item.title} item={item} />
              ))}
              {visibleSimpleNavItems.map((item) => (
                <SimpleNavItem key={item.title} item={item} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <OpenCompanyNav />
      </SidebarContent>
      <SidebarFooter>
        <SidebarUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
